import ProjectService from '@monorepo/old-web/js/services/ProjectService';
import { queryCache, useQuery } from 'react-query';
import {
  FormTemplatesCacheEvents,
  formTemplatesCacheRegister,
} from '../cacheRegisters/forms/formTemplatesCacheRegister';
import { getKeyParamsForInvalidation } from '../utils/queryUtils';

const KEY_START = 'project-calendars' as const;
const createKey = ({ projectId }: { projectId: string }) =>
  [KEY_START, projectId] as const;

const fetcher = (_: string, projectId: string) => {
  const projectService = new ProjectService();
  return projectService.getCalendars(projectId);
};

type InvalidateCacheParams = { projectId?: string };

export function useProjectCalendars({ projectId }: { projectId?: string }) {
  const isEnabled = !!projectId;
  const config = { enabled: !!projectId };
  const key = isEnabled ? createKey({ projectId }) : undefined;
  const { data, ...queryInfo } = useQuery(key, fetcher, config);

  return {
    ...queryInfo,
    projectCalendars: data,
  };
}

function removeFromCache(keyParams: InvalidateCacheParams): void {
  // Removing instead of invalidating, because any interaction with the invalid data in the meantime could lead to
  // data problems - e.g. if we show outdated calendar data while refetching, someone might go to an old version of a form template.
  // Removing is synchronous and immediately makes the outdated data unavailable.
  queryCache.removeQueries(
    getKeyParamsForInvalidation([KEY_START, keyParams.projectId]),
  );
}

const cacheRegisterInvalidator = {
  hookName: 'useProjectCalendars',
  callback: (keyParams: InvalidateCacheParams) => removeFromCache(keyParams),
};

// when form templates get updated, new versions of the form templates got saved,
// so new form templates are attached to each one of the calendars
formTemplatesCacheRegister(
  [FormTemplatesCacheEvents.UPDATE],
  cacheRegisterInvalidator,
);
